import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronLeft, ChevronRight } from 'lucide-react';

const WHATSAPP_URL =
  'https://wa.me/306955383246?text=' +
  encodeURIComponent(
    "Hello, I'm interested in the Saronic Asset beachfront property and would like more information."
  );

export default function SaronicAsset() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  // --- Section visibility tracking (view_section) ---
  useEffect(() => {
    const sectionIds = ['golden-visa', 'photo-gallery', 'plans', 'location', 'financial-picture'];
    const tracked = new Set();
    const timers = {};

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.id;
          if (entry.isIntersecting) {
            if (tracked.has(id) || timers[id]) return;
            // Require 2 seconds of dwell time before counting the view
            timers[id] = setTimeout(() => {
              tracked.add(id);
              delete timers[id];
              if (window.gtag) {
                window.gtag('event', 'view_section', {
                  section_name: id,
                  event_category: 'Engagement',
                });
              }
            }, 2000);
          } else if (timers[id]) {
            clearTimeout(timers[id]);
            delete timers[id];
          }
        });
      },
      // Fires when the section reaches the middle band of the viewport.
      // Works for sections taller than the screen, which a 0.5 threshold does not.
      { rootMargin: '-25% 0px -25% 0px' }
    );

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => {
      observer.disconnect();
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  const trackContact = (eventName, label) => {
    if (window.gtag) {
      window.gtag('event', eventName, {
        event_category: 'Contact',
        event_label: label,
      });
    }
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch('https://formspree.io/f/xyeykzaj', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
        }),
      });

      if (response.ok) {
        if (window.gtag) {
          window.gtag('event', 'generate_lead', {
            event_category: 'Contact',
            event_label: 'Investment Request Form',
            value: 2500000,
            currency: 'EUR',
          });
        }

        setFormSubmitted(true);
        setFormData({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => setFormSubmitted(false), 4000);
      } else {
        console.error('Formspree submission failed');
      }
    } catch (error) {
      console.error('Error sending form:', error);
    }
  };

  const galleryImages = [
    { src: '/images/pool-garden.jpg', alt: 'Pool and garden area' },
    { src: '/images/terrace-lounge.jpg', alt: 'Terrace lounge seating' },
    { src: '/images/living-room.jpg', alt: 'Living room' },
    { src: '/images/kitchen.jpg', alt: 'Modern kitchen' },
    { src: '/images/living-dining.jpg', alt: 'Living and dining area' },
    { src: '/images/bedroom-seaview.jpg', alt: 'Bedroom with sea view' },
  ];

  const nextImage = () => {
    setGalleryIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setGalleryIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  return (
    <div className="min-h-screen bg-white text-gray-800">
      <nav className="fixed top-0 w-full bg-white z-50 border-b border-gray-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between h-14">
            <div className="flex items-center">
              <h1 className="text-lg font-semibold tracking-tight">Saronic Asset</h1>
            </div>
            <div className="hidden md:flex items-center space-x-8 text-sm">
              <a href="#golden-visa" className="text-gray-700 hover:text-gray-900">Golden Visa</a>
              <a href="#about" className="text-gray-700 hover:text-gray-900">About</a>
              <a href="#photo-gallery" className="text-gray-700 hover:text-gray-900">Gallery</a>
              <a href="#plans" className="text-gray-700 hover:text-gray-900">Plans</a>
              <a href="#contact" className="text-gray-900 font-semibold">Get in Touch</a>
            </div>
            <div className="md:hidden flex items-center">
              <button onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu">
                {menuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-white border-t text-sm">
            <div className="px-6 py-3 space-y-2">
              <a href="#golden-visa" className="block py-2 text-gray-700" onClick={() => setMenuOpen(false)}>Golden Visa</a>
              <a href="#about" className="block py-2 text-gray-700" onClick={() => setMenuOpen(false)}>About</a>
              <a href="#photo-gallery" className="block py-2 text-gray-700" onClick={() => setMenuOpen(false)}>Gallery</a>
              <a href="#plans" className="block py-2 text-gray-700" onClick={() => setMenuOpen(false)}>Plans</a>
              <a href="#contact" className="block py-2 text-gray-900 font-semibold" onClick={() => setMenuOpen(false)}>Contact</a>
            </div>
          </div>
        )}
      </nav>

      <section className="pt-20 pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <p className="text-sm text-gray-600 mb-4">A beachfront investment opportunity</p>
            <h1 className="text-5xl lg:text-6xl font-light mb-6 leading-tight text-gray-900">
              Income today,<br />appreciation tomorrow
            </h1>
            <p className="text-lg text-gray-700 max-w-2xl leading-relaxed">
              A rare property generating immediate revenue while the location appreciates. Turnkey operations. Proven guest satisfaction. Direct owner sale—no broker fees.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <a href="#contact" className="px-6 py-3 bg-gray-900 text-white hover:bg-gray-800 transition text-center">
              Request Information
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackContact('click_whatsapp', 'WhatsApp Inbound - Hero')}
              className="px-6 py-3 border border-gray-400 text-gray-900 hover:bg-gray-50 transition text-center"
            >
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>

      <section className="px-6 mb-12">
        <div className="max-w-5xl mx-auto">
          <img src="/images/aerial-property.jpg" alt="Aerial view of Saronic asset" className="w-full rounded-sm" />
          <p className="text-sm text-gray-600 mt-3">Beachfront location in the Saronic Gulf</p>
        </div>
      </section>

      <section className="px-6 mb-16 border-y border-gray-200 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <p className="text-4xl font-light text-gray-900 mb-1">€2.5M</p>
              <p className="text-xs text-gray-600 uppercase tracking-wide">Purchase Price</p>
            </div>
            <div>
              <p className="text-4xl font-light text-gray-900 mb-1">~5%</p>
              <p className="text-xs text-gray-600 uppercase tracking-wide">Annual Yield</p>
            </div>
            <div>
              <p className="text-4xl font-light text-gray-900 mb-1">3-5%</p>
              <p className="text-xs text-gray-600 uppercase tracking-wide">Market Growth</p>
            </div>
            <div>
              <p className="text-4xl font-light text-gray-900 mb-1">0%</p>
              <p className="text-xs text-gray-600 uppercase tracking-wide">Broker Fees</p>
            </div>
          </div>
        </div>
      </section>

      {/* --- Golden Visa --- */}
      <section id="golden-visa" className="px-6 mb-16 bg-gray-50 py-12">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-light mb-8 text-gray-900">Eligible for up to three Golden Visas</h2>

          <div className="max-w-3xl space-y-6 text-gray-700 leading-relaxed mb-12">
            <p>
              The Greek Golden Visa threshold for Attica is €800,000 per applicant, in a single property of at least 120 m². This asset is €2.5M across 460 m²—comfortably clear on both counts.
            </p>
            <p>
              Structured as a joint acquisition, up to three co-investors can each hold a qualifying share and apply independently. That makes this suitable for a smaller investor group as well as a single buyer, with each participant securing EU residency for themselves and their immediate family.
            </p>
            <p className="text-gray-900">For a sole purchaser, the asset qualifies outright.</p>
          </div>

          {/* Three qualifying shares */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
            {[1, 2, 3].map((n) => (
              <div key={n} className="bg-white border border-gray-200 rounded-sm p-6">
                <div className="flex items-start justify-between mb-5">
                  <svg viewBox="0 0 40 28" className="w-10 h-7" aria-hidden="true">
                    <rect x="0.5" y="0.5" width="39" height="27" rx="2" fill="none" stroke="#111827" strokeWidth="1" />
                    <circle cx="11" cy="11" r="4" fill="none" stroke="#111827" strokeWidth="1" />
                    <path d="M5 21c1.5-3 10.5-3 12 0" fill="none" stroke="#111827" strokeWidth="1" strokeLinecap="round" />
                    <line x1="22" y1="9" x2="34" y2="9" stroke="#9CA3AF" strokeWidth="1" strokeLinecap="round" />
                    <line x1="22" y1="14" x2="34" y2="14" stroke="#9CA3AF" strokeWidth="1" strokeLinecap="round" />
                    <line x1="22" y1="19" x2="29" y2="19" stroke="#9CA3AF" strokeWidth="1" strokeLinecap="round" />
                  </svg>
                  <span className="text-xs text-gray-500 uppercase tracking-wide">Share {n}</span>
                </div>
                <p className="text-3xl font-light text-gray-900 mb-1">€833,000</p>
                <p className="text-xs text-gray-600 uppercase tracking-wide mb-4">Qualifying share</p>
                <p className="text-sm text-gray-700 pt-4 border-t border-gray-200">
                  One residency permit · applicant + family
                </p>
              </div>
            ))}
          </div>

          {/* Threshold comparison */}
          <div className="bg-white border border-gray-200 rounded-sm p-8">
            <h3 className="font-semibold text-gray-900 mb-6">How it clears the thresholds</h3>

            <div className="space-y-7">
              <div>
                <div className="flex justify-between items-baseline mb-2 text-sm">
                  <span className="text-gray-700">Value per qualifying share</span>
                  <span className="text-gray-900 font-medium">€833,000</span>
                </div>
                <div className="relative h-2 bg-gray-100 rounded-sm">
                  <div className="absolute inset-y-0 left-0 bg-gray-900 rounded-sm" style={{ width: '100%' }} />
                  <div className="absolute inset-y-0 w-px bg-gray-400" style={{ left: '96%' }} />
                </div>
                <p className="text-xs text-gray-600 mt-2">Attica minimum: €800,000 per applicant</p>
              </div>

              <div>
                <div className="flex justify-between items-baseline mb-2 text-sm">
                  <span className="text-gray-700">Built area</span>
                  <span className="text-gray-900 font-medium">460 m²</span>
                </div>
                <div className="relative h-2 bg-gray-100 rounded-sm">
                  <div className="absolute inset-y-0 left-0 bg-gray-900 rounded-sm" style={{ width: '100%' }} />
                  <div className="absolute inset-y-0 w-px bg-gray-400" style={{ left: '26%' }} />
                </div>
                <p className="text-xs text-gray-600 mt-2">Minimum size: 120 m² of main areas</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8 pt-8 border-t border-gray-200 text-sm">
              <div>
                <p className="text-xs text-gray-600 uppercase tracking-wide mb-1">Zone</p>
                <p className="text-gray-900">Attica · €800,000 tier</p>
              </div>
              <div>
                <p className="text-xs text-gray-600 uppercase tracking-wide mb-1">Permit duration</p>
                <p className="text-gray-900">5 years, renewable</p>
              </div>
              <div>
                <p className="text-xs text-gray-600 uppercase tracking-wide mb-1">Covers</p>
                <p className="text-gray-900">Applicant, spouse, children</p>
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-600 italic mt-6 max-w-3xl">
            Golden Visa eligibility depends on the acquisition structure and applicant circumstances. We will provide full documentation for your legal advisers to review.
          </p>
        </div>
      </section>

      <section id="about" className="px-6 mb-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-light mb-8 text-gray-900">What you're buying</h2>
          <div className="space-y-8 text-gray-700 leading-relaxed">
            <p>This property is unusual. Most real estate investors face a choice: buy property and hope it appreciates, or buy a business and collect income. Here, you get both.</p>
            <p>The property generates roughly 5% annual income right now, from an established short-term rental operation with excellent guest reviews. At the same time, it's located in the Saronic Gulf—where property values are appreciating 3-5% annually. That's roughly 8-10% total return potential on a tangible asset you own.</p>
            <p>The operations are proven. Guests are satisfied. The booking channels are established. All the systems transfer with the sale. You could acquire this and have income coming in within days.</p>
            <p className="text-lg font-light pt-4 border-t border-gray-200">Most importantly: beachfront properties generating immediate income in growth markets are rare. Very rare.</p>
          </div>
        </div>
      </section>

      <section id="photo-gallery" className="px-6 mb-16 bg-gray-50 py-12">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-light mb-12 text-gray-900">Photo Gallery</h2>
          <div className="relative mb-8">
            <img src={galleryImages[galleryIndex].src} alt={galleryImages[galleryIndex].alt} className="w-full rounded-sm" />
            <button onClick={prevImage} aria-label="Previous image" className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded hover:bg-opacity-70">
              <ChevronLeft size={24} />
            </button>
            <button onClick={nextImage} aria-label="Next image" className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded hover:bg-opacity-70">
              <ChevronRight size={24} />
            </button>
            <p className="text-sm text-gray-600 mt-3">{galleryIndex + 1} of {galleryImages.length}</p>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {galleryImages.map((img, idx) => (
              <img key={idx} src={img.src} alt={img.alt} onClick={() => setGalleryIndex(idx)} className={`w-full rounded-sm cursor-pointer opacity-60 hover:opacity-100 transition ${idx === galleryIndex ? 'opacity-100 border-2 border-gray-900' : ''}`} />
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 mb-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-light mb-8 text-gray-900">Beach access & outdoor amenities</h2>
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <img src="/images/beach-sunset.jpg" alt="Beach with sunset and amenities" className="w-full rounded-sm mb-4" />
              <p className="text-xs text-gray-600">Direct beach access with premium facilities</p>
            </div>
            <div className="text-gray-700">
              <p className="mb-6 leading-relaxed">The property includes direct access to a premium beach area just meters away through a private underpass. This exclusive beachfront location features high-end lounging facilities, umbrellas, and bean bag seating—providing guests with a luxury beach club experience without leaving the property.</p>
              <div className="space-y-4 text-sm">
                <div>
                  <p className="font-semibold text-gray-900">Direct Beach Access</p>
                  <p className="text-gray-700 mt-1">Private underpass connects property directly to beach (30-40m)</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Premium Facilities</p>
                  <p className="text-gray-700 mt-1">Lounge chairs, umbrellas, bean bags, and sunbeds</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Guest Appeal</p>
                  <p className="text-gray-700 mt-1">Competitive advantage over neighboring properties. Drives booking demand.</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Revenue Opportunity</p>
                  <p className="text-gray-700 mt-1">Premium pricing justified by unmatched beach access and facilities</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="plans" className="px-6 mb-16 bg-gray-50 py-12">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-light mb-12 text-gray-900">Property Layout & Plans</h2>
          <div className="space-y-12">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Ground Floor</h3>
              <img src="/images/plan-ground-floor.jpg" alt="Ground floor plan" className="w-full rounded-sm" />
              <p className="text-sm text-gray-600 mt-2">Apartments #2 & #4 - Garden view units with direct terrace access</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">First Floor</h3>
              <img src="/images/plan-first-floor.jpg" alt="First floor plan" className="w-full rounded-sm" />
              <p className="text-sm text-gray-600 mt-2">Apartments #1 & #3 - Sea view units with balconies overlooking the Aegean</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Second Floor</h3>
              <img src="/images/plan-second-floor.jpg" alt="Second floor plan" className="w-full rounded-sm" />
              <p className="text-sm text-gray-600 mt-2">Bedrooms for upper-level units with private balcony spaces</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Semi-Basement Level</h3>
              <img src="/images/plan-semi-basement.jpg" alt="Semi-basement plan" className="w-full rounded-sm" />
              <p className="text-sm text-gray-600 mt-2">Additional bedroom and support spaces for flexible property use</p>
            </div>
          </div>
          <div className="mt-12 p-8 bg-white rounded-sm border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-3">Property Configuration</h3>
            <p className="text-gray-700 text-sm">Four completely independent units on a 1,805 sqm plot provide maximum flexibility. Each unit can operate as a standalone rental or be combined for larger group bookings. The multi-level design maximizes views and privacy while maintaining shared amenities like the pool and gardens.</p>
          </div>
        </div>
      </section>

      <section id="location" className="px-6 mb-16">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-light mb-8 text-gray-900">Property Location & Geography</h2>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Topographical View</h3>
              <img src="/images/map-topography.jpg" alt="Topographical map" className="w-full rounded-sm" />
              <p className="text-sm text-gray-600 mt-2">Terrain and elevation showing property positioning</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Area Map</h3>
              <img src="/images/map-view.jpg" alt="Area map view" className="w-full rounded-sm" />
              <p className="text-sm text-gray-600 mt-2">Location context within Saronic Gulf region</p>
            </div>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-8">
            <div className="p-8 bg-gray-50 rounded-sm">
              <h3 className="font-semibold text-gray-900 mb-3">Key Distances</h3>
              <div className="space-y-2 text-sm text-gray-700">
                <p><span className="font-medium text-gray-900">15 min</span> to Athens International Airport</p>
                <p><span className="font-medium text-gray-900">25 min</span> to Voula Metro station</p>
                <p><span className="font-medium text-gray-900">30 min</span> to Cape Sounion</p>
                <p><span className="font-medium text-gray-900">40 min</span> to Athens city center</p>
              </div>
            </div>
            <div className="p-8 bg-gray-50 rounded-sm">
              <h3 className="font-semibold text-gray-900 mb-3">Market Context</h3>
              <p className="text-sm text-gray-700">Positioned in the Saronic Gulf's fastest-growing market segment. Adjacent to luxury resort developments and major 5-star hotels under construction. Prime location for both tourism recovery and property appreciation.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="financial-picture" className="px-6 mb-16 bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-light mb-8 text-gray-900">The financial picture</h2>
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="font-semibold text-gray-900 mb-6">Current revenue</h3>
              <div className="space-y-5 text-gray-700 text-sm">
                <div>
                  <p className="font-medium text-gray-900">Short-term rentals</p>
                  <p>Primary income source. Established booking channels. Consistent occupancy.</p>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Event hosting</p>
                  <p>Corporate groups and celebrations. Premium pricing. Additional margin.</p>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Operating costs</p>
                  <p>Minimal. Systems are optimized. No major capital expenditure needed.</p>
                </div>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-6">Your return</h3>
              <div className="bg-white border border-gray-200 rounded-sm p-8 space-y-6">
                <div className="border-b border-gray-200 pb-4">
                  <p className="text-xs text-gray-600 uppercase mb-2">Investment</p>
                  <p className="text-3xl font-light text-gray-900">€2.5M</p>
                </div>
                <div className="border-b border-gray-200 pb-4">
                  <p className="text-xs text-gray-600 uppercase mb-2">Annual yield</p>
                  <p className="text-5xl font-light text-gray-900">~5%</p>
                </div>
                <div className="border-b border-gray-200 pb-4">
                  <p className="text-xs text-gray-600 uppercase mb-2">Market appreciation</p>
                  <p className="text-5xl font-light text-gray-900">3-5%</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 uppercase mb-2">Combined potential</p>
                  <p className="text-5xl font-light text-gray-900">8-10%</p>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-12 p-8 bg-white rounded-sm border border-gray-200">
            <p className="text-gray-700 leading-relaxed mb-4">Let's be clear about why this matters. Government bonds in Greece are yielding 3-4%. Most real estate returns 2-3%. This property is generating roughly 5% in immediate cash flow, plus 3-5% in property appreciation—8-10% total.</p>
            <p className="text-gray-700 leading-relaxed">And unlike a pure investment property (no income) or a pure business (no asset), you're acquiring both. A tangible asset that's producing cash today and appreciating tomorrow.</p>
          </div>
        </div>
      </section>

      <section className="px-6 mb-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-light mb-12 text-gray-900">What guests think</h2>
          <div className="space-y-8">
            <div className="border-l-4 border-gray-400 pl-6 py-2">
              <p className="text-gray-700 mb-3 italic leading-relaxed">"Absolutely everything, this complex is perfect for families. There is so much space in all the apartments, the grounds and the pool area. The owner could not have done any more for us. Plenty of amenities around—coffee shops, market and restaurants. I would recommend this place to anyone 100%. I will be back."</p>
              <p className="text-sm font-semibold text-gray-900">Mary, Ireland</p>
            </div>
            <div className="border-l-4 border-gray-400 pl-6 py-2">
              <p className="text-gray-700 mb-3 italic leading-relaxed">"Excellent place and well run family business highly recommend! We stayed there for one week. Apartment itself was beautiful and spacious with amazing sea views. One of the best beaches in lagonisi with pristine water was on front of the villa 1 minute walk through a tunnel linked from villa to beach! It feels almost as your going to your own private beach."</p>
              <p className="text-sm font-semibold text-gray-900">Arsen, Ireland</p>
            </div>
            <div className="border-l-4 border-gray-400 pl-6 py-2">
              <p className="text-gray-700 mb-3 italic leading-relaxed">"The villa is very comfortable. There is everything you need for a family vacation with a small child. The pool area is very relaxing, and a convenient underpass takes you directly to the beach! The owners were very kind and accommodating!"</p>
              <p className="text-sm font-semibold text-gray-900">Roberta, Italy</p>
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-12">Real feedback from verified guests. Professional management, strong family market appeal, and repeat business drive consistent occupancy and revenue.</p>
        </div>
      </section>

      <section className="px-6 mb-16 bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-light mb-8 text-gray-900">Ready to close</h2>
          <div className="grid md:grid-cols-3 gap-8 text-sm text-gray-700">
            <div>
              <p className="font-semibold text-gray-900 mb-2">Documentation</p>
              <p>All legal files prepared and compliant with current Greek requirements. Digital copies organized. Ready for review.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 mb-2">No broker involved</p>
              <p>Direct negotiation with owner. No realtor commission. Transparent deal. Potentially significant cost savings.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 mb-2">Fast timeline</p>
              <p>Pre-prepared documentation means quicker closing. Professional seller. Minimal due diligence delays.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="contact" className="px-6 mb-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-light mb-8 text-gray-900">Get in touch</h2>
          <p className="text-gray-700 mb-12 leading-relaxed max-w-2xl">If this resonates, let's talk. Request the full investment brief, financial details, and schedule a time to see the property.</p>

          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div className="flex flex-col gap-3">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackContact('click_whatsapp', 'WhatsApp Inbound')}
                  className="px-6 py-3 bg-gray-900 text-white hover:bg-gray-800 transition text-center font-semibold text-sm"
                >
                  Chat on WhatsApp
                </a>
                <a
                  href="mailto:info@progrecia.gr?subject=Saronic%20Asset%20-%20Investment%20Enquiry"
                  onClick={() => trackContact('click_email', 'Direct Email Inbound')}
                  className="px-6 py-3 border border-gray-400 text-gray-900 hover:bg-gray-50 transition text-center font-semibold text-sm"
                >
                  Email us directly
                </a>
              </div>

              <div className="space-y-6">
                <div>
                  <p className="text-xs text-gray-600 uppercase mb-2">Email</p>
                  <a
                    href="mailto:info@progrecia.gr?subject=Saronic%20Asset%20-%20Investment%20Enquiry"
                    onClick={() => trackContact('click_email', 'Direct Email Inbound')}
                    className="text-lg text-gray-900 hover:text-gray-600 transition"
                  >
                    info@progrecia.gr
                  </a>
                </div>
                <div>
                  <p className="text-xs text-gray-600 uppercase mb-2">Phone / WhatsApp</p>
                  <a
                    href="tel:+306955383246"
                    onClick={() => trackContact('click_phone', 'Phone Call Inbound')}
                    className="text-lg text-gray-900 hover:text-gray-600 transition"
                  >
                    +30 695 538 3246
                  </a>
                </div>
                <div>
                  <p className="text-xs text-gray-600 uppercase mb-2">Direct Sale</p>
                  <p className="text-gray-700">Owner-negotiated. No intermediaries. Transparent process.</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleFormSubmit} className="space-y-4">
              <input type="text" name="name" placeholder="Your Name" value={formData.name} onChange={handleFormChange} className="w-full px-4 py-2 border border-gray-300 rounded-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-400" required />
              <input type="email" name="email" placeholder="Email" value={formData.email} onChange={handleFormChange} className="w-full px-4 py-2 border border-gray-300 rounded-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-400" required />
              <input type="tel" name="phone" placeholder="Phone (Optional)" value={formData.phone} onChange={handleFormChange} className="w-full px-4 py-2 border border-gray-300 rounded-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-400" />
              <textarea name="message" placeholder="What would you like to know?" value={formData.message} onChange={handleFormChange} rows="4" className="w-full px-4 py-2 border border-gray-300 rounded-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-400 resize-none" />
              <button type="submit" className="w-full bg-gray-900 text-white py-3 hover:bg-gray-800 transition font-semibold text-sm">Send Message</button>
              {formSubmitted && <p className="text-gray-600 text-center text-sm">Thanks. We'll be in touch soon.</p>}
            </form>
          </div>
        </div>
      </section>

      <footer className="border-t border-gray-200 py-8 px-6 text-center text-xs text-gray-600">
        <p className="mb-2">© 2024 Saronic Asset. All content is the property of Progrecia Development and Services O.E.</p>
        <p>Investment opportunity presentation. Conduct your own due diligence and consult professional advisors.</p>
      </footer>
    </div>
  );
}
